import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
} from 'react';
import type {
  ChangeEvent,
  ClipboardEvent,
  KeyboardEvent,
  ReactNode,
} from 'react';
import styles from './CodeInput.module.css';

export interface CodeInputProps {
  /** Number of cells. Default 6 (TOTP standard). */
  length?: number;
  /** Current code value (controlled). Length-padded to `length` on render. */
  value: string;
  /** Called on every change with the new (possibly partial) code. */
  onChange: (next: string) => void;
  /** Called when the user has filled every cell. Use this for auto-submit. */
  onComplete?: (code: string) => void;
  /** Accessible group label. Required for screen readers. */
  ariaLabel: string;
  /** Optional id base (each cell becomes `${id}-cell-N`). */
  id?: string;
  /** Disable all cells (e.g. while submitting). */
  disabled?: boolean;
  /** Error state — shows danger border + announces. */
  errorMessage?: ReactNode;
  /** Whether to autofocus the first empty cell on mount. Default true. */
  autoFocus?: boolean;
  /** Restrict to digits only (default) or allow any char (recovery codes). */
  inputMode?: 'numeric' | 'text';
}

/**
 * Six-cell one-time-code input.
 *
 * Behavior:
 *  - Typing in a cell auto-advances to the next.
 *  - Backspace on an empty cell moves focus left and clears that previous cell.
 *  - Arrow Left/Right navigates between cells.
 *  - Pasting a value of length >= `length` fills all cells (digits-only when
 *    `inputMode='numeric'`); auto-submits if a complete code results.
 *  - `autocomplete="one-time-code"` on the first cell so iOS/Android SMS code
 *    autofill works on mobile.
 *
 * Renders as a single labeled group (`role="group"` + `aria-label`); errors
 * are announced via `aria-live="assertive"`.
 */
export function CodeInput({
  length = 6,
  value,
  onChange,
  onComplete,
  ariaLabel,
  id,
  disabled = false,
  errorMessage,
  autoFocus = true,
  inputMode = 'numeric',
}: CodeInputProps) {
  const generatedId = useId();
  const baseId = id ?? generatedId;
  const cellRefs = useRef<Array<HTMLInputElement | null>>([]);

  const cells = useMemo(() => {
    const padded = value.padEnd(length, ' ').slice(0, length);
    return Array.from({ length }, (_, i) => {
      const ch = padded[i];
      return ch === ' ' ? '' : ch;
    });
  }, [value, length]);

  const sanitize = useCallback(
    (raw: string) => {
      if (inputMode === 'numeric') return raw.replace(/\D/g, '');
      // For recovery codes etc.: drop whitespace, keep alphanumerics + dashes.
      return raw.replace(/[^\w-]/g, '');
    },
    [inputMode],
  );

  // Auto-focus the first empty cell on mount.
  useEffect(() => {
    if (!autoFocus) return;
    const firstEmpty = cells.findIndex((c) => c === '');
    const idx = firstEmpty === -1 ? length - 1 : firstEmpty;
    cellRefs.current[idx]?.focus();
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCellChange = (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
    const incoming = sanitize(event.target.value);

    // If the user typed multiple chars (e.g. pasted into one cell), spill into
    // subsequent cells.
    const next = cells.slice();
    let cursor = index;
    for (const ch of incoming) {
      if (cursor >= length) break;
      next[cursor] = ch;
      cursor += 1;
    }
    const nextValue = next.join('').trimEnd();
    onChange(nextValue);

    const focusTarget = Math.min(cursor, length - 1);
    if (focusTarget !== index) cellRefs.current[focusTarget]?.focus();

    if (next.every((c) => c !== '')) {
      onComplete?.(next.join(''));
    }
  };

  const handleKeyDown = (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      const current = cells[index];
      if (current) {
        // Clear current cell only.
        const next = cells.slice();
        next[index] = '';
        onChange(next.join('').trimEnd());
        return;
      }
      // Empty cell: move left and clear that one.
      if (index > 0) {
        event.preventDefault();
        const next = cells.slice();
        next[index - 1] = '';
        onChange(next.join('').trimEnd());
        cellRefs.current[index - 1]?.focus();
      }
      return;
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      cellRefs.current[index - 1]?.focus();
      return;
    }
    if (event.key === 'ArrowRight' && index < length - 1) {
      event.preventDefault();
      cellRefs.current[index + 1]?.focus();
      return;
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const raw = event.clipboardData.getData('text');
    const cleaned = sanitize(raw);
    if (!cleaned) return;
    event.preventDefault();
    const next = cleaned.slice(0, length).padEnd(length, ' ').split('');
    const finalArr = next.map((c) => (c === ' ' ? '' : c));
    const filled = finalArr.join('').trimEnd();
    onChange(filled);
    const focusTarget = Math.min(cleaned.length, length - 1);
    cellRefs.current[focusTarget]?.focus();
    if (finalArr.every((c) => c !== '')) {
      onComplete?.(finalArr.join(''));
    }
  };

  const messageId = `${baseId}-msg`;

  return (
    <div className={styles.wrapper}>
      <div
        role="group"
        aria-label={ariaLabel}
        aria-describedby={errorMessage ? messageId : undefined}
        className={[styles.group, errorMessage ? styles.groupError : null]
          .filter(Boolean)
          .join(' ')}
      >
        {cells.map((cellValue, i) => (
          <input
            key={i}
            ref={(el) => {
              cellRefs.current[i] = el;
            }}
            id={`${baseId}-cell-${i}`}
            type="text"
            inputMode={inputMode}
            // Only the first cell carries autocomplete=one-time-code per WHATWG;
            // mobile OS autofill targets that cell and we spill the rest.
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            maxLength={length}
            value={cellValue}
            disabled={disabled}
            onChange={handleCellChange(i)}
            onKeyDown={handleKeyDown(i)}
            onPaste={handlePaste}
            onFocus={(e) => e.currentTarget.select()}
            className={[
              styles.cell,
              cellValue ? styles.cellFilled : null,
              errorMessage ? styles.cellError : null,
            ]
              .filter(Boolean)
              .join(' ')}
            aria-label={`Digit ${i + 1} of ${length}`}
            aria-invalid={errorMessage ? true : undefined}
          />
        ))}
      </div>
      {errorMessage && (
        <p id={messageId} className={styles.errorMessage} role="alert" aria-live="assertive">
          {errorMessage}
        </p>
      )}
    </div>
  );
}
